import React, {
  useEffect,
  useRef,
  forwardRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useCharacterCounter } from 'hooks/useCharacterCounter/useCharacterCounter';
import { mergeRefs } from 'utils/mergeRefs';
import { ErrorText } from 'view/atoms/Input';
import { formatTextWithUrls } from 'utils';
import { ITextAreaProps } from './TextArea.model';
import * as S from './TextArea.styles';
import CharacterCounter from '../CharacterCounter/CharacterCounter';

export const TextArea = forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  (
    {
      onChange,
      onBlur,
      name,
      disabled,
      error,
      subLabel,
      placeholder,
      icon,
      value,
      maxLength,
      hasCounter,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const [displayValue, setDisplayValue] = useState(value || '');

    const {
      currentLength,
      maxLength: counterMaxLength,
      isError: counterIsError,
    } = useCharacterCounter(displayValue, maxLength);

    const highlightText = useCallback(
      (text: string): React.ReactNode[] =>
        formatTextWithUrls(text, { asLink: false }),
      [],
    );

    const highlightedNodes = useMemo(
      () => highlightText(displayValue),
      [displayValue],
    );

    useEffect(() => {
      setDisplayValue(value || '');
    }, [value]);

    useEffect(() => {
      if (textAreaRef.current) {
        const textArea = textAreaRef.current;

        textArea.style.height = '120px';
        const borderHeight = textArea.offsetHeight - textArea.clientHeight;
        textArea.style.height = `${textArea.scrollHeight + borderHeight}px`;
      }
    }, [displayValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDisplayValue(e.target.value);
      onChange?.(e); // Pass the original event to the parent onChange
    };

    return (
      <S.TextAreaWrapper>
        <S.StyledTextOverlay>{highlightedNodes}</S.StyledTextOverlay>

        <S.StyledTextArea
          value={displayValue}
          maxLength={maxLength}
          name={name}
          onChange={handleChange}
          onBlur={onBlur}
          error={error}
          disabled={disabled}
          ref={mergeRefs([textAreaRef, ref])}
          placeholder={placeholder}
          data-testid={dataTestId}
        />

        {hasCounter && (
          <CharacterCounter
            currentLength={currentLength}
            maxLength={counterMaxLength}
            isError={counterIsError}
          />
        )}

        {error && <ErrorText error={error} />}
        {subLabel && !error && <S.StyledSubLabel>{subLabel}</S.StyledSubLabel>}

        {icon && <S.IconBox>{icon}</S.IconBox>}
      </S.TextAreaWrapper>
    );
  },
);
